import React, { useEffect, useMemo, useState } from "react";
import { Card, Switch } from "antd";
import { deleteExam, freezeExam, getExamList } from "../../../../services/examination";
import { Tag } from "antd";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import CustomTable from "../../../../common/custom-table/custom-table";
import CustomPopover from "../../../../common/custom-popover/custom-popover";
import SVGIcon from "../../../../constants/SVGIcon";

const columns = (onViewClick, handleDeleteExam, handleToggleFreeze) => {
  return [
    {
      title: "Examination Name",
      dataIndex: "exam_name",
      key: "exam_name",
      sorter: true,
    },
    {
      title: "Examination Type",
      dataIndex: "examination_type",
      key: "examination_type",
      sorter: true,
    },
    {
      title: "Exam Start Date",
      dataIndex: "exam_start_date",
      key: "exam_start_date",
      sorter: true,
    },
    {
      title: "Exam End Date",
      dataIndex: "exam_end_date",
      key: "exam_end_date",
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      render: (text) => (
        <Tag
          color={
            text === "Completed"
              ? "green"
              : text === "Ongoing"
              ? "blue"
              : "yellow"
          }
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Freeze",
      dataIndex: "is_freeze",
      key: "is_freeze",
      render: (value, record) => (
        <Switch
          checked={value}
          onChange={(checked) => handleToggleFreeze(record?.id, checked)}
        />
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <span>
          <span style={{ marginRight: 8 }}>
            <EyeOutlined
              onClick={onViewClick.bind(null, record)}
              style={{ color: "#1E9EFD", fontSize: 24 }}
            />
          </span>
          <span style={{ marginRight: 8 }}>
            <EditOutlined
              onClick={onViewClick.bind(null, record)}
              style={{ color: "#3BB44B", fontSize: 24 }}
            />
          </span>
          {record?.status === "Upcoming" ? (
            <CustomPopover
              message="Are you sure you want to delete?"
              confirmText="Delete"
              cancelText="Cancel"
              onConfirm={handleDeleteExam.bind(null, record?.id)}
            >
              <SVGIcon
                name={"delete"}
                width={"20"}
                height={"20"}
                stroke={"rgba(243, 17, 17, 1)"}
              />
            </CustomPopover>
          ) : (
            <SVGIcon
              name="delete"
              width="20"
              height="20"
              stroke="rgba(0, 0, 0, 0.25)"
              style={{ cursor: "not-allowed" }}
            />
          )}
        </span>
      ),
    },
  ];
};

const ExamListTable = ({ onViewClick, selectedYear }) => {
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchExamList = async (tempPage = pageNo, tempPageSize = pageSize) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
        academic_year_id: selectedYear
      };
      const resp = await getExamList(payload);
      setDataSource(resp?.data?.data);
      setTotalCount(resp?.data?.totalRecords);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExam = async (examId) => {
    try {
      setLoading(true);
      await deleteExam(examId);
      fetchExamList();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFreeze = async (examId, checked) => {
    try {
      setLoading(true);
      setDataSource((prev) =>
        prev.map((item) =>
          item?.id === examId ? { ...item, is_freeze: checked } : item
        )
      );
      const payload = {
        exam_id: examId,
        is_freeze: checked
      };
      await freezeExam(payload);
      await fetchExamList();
    } catch (err) {
      console.log(err);
      setDataSource((prev) =>
        prev.map((item) =>
          item?.id === examId ? { ...item, is_freeze: !checked } : item
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    fetchExamList(page, pageSize);
  };

  useEffect(() => {
    if (selectedYear) {
      setPageNo(1);
      fetchExamList();
    }
  }, [selectedYear]);

  const tableColumns = useMemo(() => columns(onViewClick, handleDeleteExam, handleToggleFreeze), []);

  return (
    <Card>
      <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 16 }}>
        Examination List
      </div>
      <CustomTable
        headerColor="#667085"
        headerBackgroundColor="#F9FAFB"
        dataSource={dataSource}
        columns={tableColumns}
        loading={loading}
        total={totalCount}
        pageSize={pageSize}
        currentPage={pageNo}
        handlePageChange={handleChangePage}
      />
    </Card>
  );
};

export default ExamListTable;
